import React, { useMemo, useState, useRef, useCallback } from "react";
import { SCHEDULE_ROW_HEIGHT } from "./scheduleConstants";
import {
  Switch,
  Select,
  Space,
  Table,
  Typography,
  Button,
  Tooltip,
  Popconfirm,
  Divider,
} from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ScheduleGanttChart from "./ScheduleGanttChart";
import AskAIButton from "../help/AskAIButton";
import {
  flattenScheduleTree,
  formatDuration,
  formatScheduleDate,
} from "../../utils/xerParser";

const { Text } = Typography;

const tablePanelClassName =
  "flex min-h-0 flex-col border-r border-gray-200 max-[1100px]:border-r-0 max-[1100px]:border-b max-[1100px]:border-gray-200 [&_.ant-table-thead>tr>th]:!bg-gray-50 [&_.ant-table-tbody>tr>td]:box-border [&_.ant-table-tbody>tr>td]:!py-[7px]";

const tableScrollClassName =
  "min-h-0 flex-1 overflow-auto pr-1 pb-[18px] [scrollbar-gutter:stable_both-edges] box-content";

const ScheduleViewer = ({
  schedule,
  fileName,
  projectName,
  onUploadClick,
  onDownload,
  onDelete,
  isDeleting,
  isUploading,
}) => {
  const [expandedKeys, setExpandedKeys] = useState(schedule.expandedKeys || []);
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const [showPercentComplete, setShowPercentComplete] = useState(true);
  const [showDependencies, setShowDependencies] = useState(true);
  const [timeScale, setTimeScale] = useState("week");
  const [zoom, setZoom] = useState(1);
  const tableScrollRef = useRef(null);
  const ganttScrollRef = useRef(null);
  const syncingScroll = useRef(false);

  const visibleRows = useMemo(
    () => flattenScheduleTree(schedule.tree, expandedKeys),
    [schedule.tree, expandedKeys]
  );

  const tableHeight = Math.max(420, Math.round(560 * zoom));
  const tableBodyHeight = tableHeight;

  const columns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 96,
        render: (value) => (
          <Text className="font-mono text-xs text-slate-600">{value}</Text>
        ),
      },
      {
        title: "Task",
        dataIndex: "name",
        key: "name",
        width: 320,
        ellipsis: true,
        render: (value, record) => (
          <span
            className={
              record.isSummary
                ? "font-semibold text-gray-900"
                : "font-normal text-gray-700"
            }
          >
            {value}
          </span>
        ),
      },
      {
        title: "Start",
        dataIndex: "start",
        key: "start",
        width: 112,
        render: (value) => formatScheduleDate(value),
      },
      {
        title: "Finish",
        dataIndex: "end",
        key: "end",
        width: 112,
        render: (value) => formatScheduleDate(value),
      },
      {
        title: "Duration",
        dataIndex: "durationDays",
        key: "durationDays",
        width: 88,
        render: (value, record) => formatDuration(value, record.taskType),
      },
      ...(showPercentComplete
        ? [
            {
              title: "%",
              dataIndex: "percentComplete",
              key: "percentComplete",
              width: 72,
              render: (value) =>
                value === null || value === undefined ? "—" : `${value}`,
            },
          ]
        : []),
    ],
    [showPercentComplete]
  );

  const tableScrollX = useMemo(
    () => columns.reduce((sum, column) => sum + (column.width || 100), 0) + 48,
    [columns]
  );

  const getTableHeaderOffset = useCallback((tableScrollEl) => {
    return (
      tableScrollEl?.querySelector(".ant-table-thead")?.offsetHeight ?? 0
    );
  }, []);

  const syncScroll = useCallback(
    (source, target, { sourceIsTable }) => {
      if (!source || !target || syncingScroll.current) return;
      syncingScroll.current = true;

      const tableEl = sourceIsTable ? source : target;
      const headerOffset = getTableHeaderOffset(tableEl);

      if (sourceIsTable) {
        target.scrollTop = Math.max(0, source.scrollTop - headerOffset);
      } else {
        target.scrollTop = source.scrollTop + headerOffset;
      }

      requestAnimationFrame(() => {
        syncingScroll.current = false;
      });
    },
    [getTableHeaderOffset]
  );

  const handleTableScroll = useCallback(
    (event) => {
      syncScroll(event.target, ganttScrollRef.current, { sourceIsTable: true });
    },
    [syncScroll]
  );

  const handleGanttScroll = useCallback(
    (event) => {
      syncScroll(event.target, tableScrollRef.current, { sourceIsTable: false });
    },
    [syncScroll]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold mb-1">Schedule</h1>
          <Text type="secondary" className="block text-sm">
            {schedule.projectName}
            {projectName && ` · ${projectName}`}
            {` · ${fileName} · ${schedule.stats.taskCount} tasks · ${schedule.stats.dependencyCount} links`}
          </Text>
        </div>
        <Space wrap>
          <AskAIButton />
          <Button icon={<DownloadOutlined />} onClick={onDownload}>
            Download
          </Button>
          <Popconfirm
            title="Remove schedule?"
            description="This deletes the uploaded file from the project."
            onConfirm={onDelete}
            okText="Remove"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: isDeleting }}
          >
            <Button danger icon={<DeleteOutlined />} loading={isDeleting}>
              Remove
            </Button>
          </Popconfirm>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={onUploadClick}
            loading={isUploading}
          >
            Replace file
          </Button>
        </Space>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border border-gray-200 rounded px-4 py-3">
        <div className="flex flex-wrap items-center gap-4">
          <Text className="text-sm text-gray-600">Display</Text>
          <Space wrap size="large">
            <span className="inline-flex items-center gap-2 text-[13px] text-gray-700">
              <Switch
                size="small"
                checked={showCriticalPath}
                onChange={setShowCriticalPath}
              />
              Critical path
            </span>
            <span className="inline-flex items-center gap-2 text-[13px] text-gray-700">
              <Switch
                size="small"
                checked={showPercentComplete}
                onChange={setShowPercentComplete}
              />
              Progress
            </span>
            <span className="inline-flex items-center gap-2 text-[13px] text-gray-700">
              <Switch
                size="small"
                checked={showDependencies}
                onChange={setShowDependencies}
              />
              Dependencies
            </span>
          </Space>
        </div>

        <Divider type="vertical" className="!m-0 !h-7 max-[1100px]:hidden" />

        <Space wrap>
          <Select
            value={timeScale}
            onChange={setTimeScale}
            style={{ width: 112 }}
            options={[
              { value: "day", label: "Daily" },
              { value: "week", label: "Weekly" },
              { value: "month", label: "Monthly" },
            ]}
          />
          <Tooltip title="Zoom in">
            <Button
              icon={<ZoomInOutlined />}
              onClick={() => setZoom((z) => Math.min(z + 0.1, 1.6))}
            />
          </Tooltip>
          <Tooltip title="Zoom out">
            <Button
              icon={<ZoomOutOutlined />}
              onClick={() => setZoom((z) => Math.max(z - 0.1, 0.7))}
            />
          </Tooltip>
        </Space>
      </div>

      <div className="mb-1 grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-visible border border-gray-200 min-[1101px]:grid-cols-[minmax(400px,44%)_minmax(360px,1fr)]">
        <div
          className={tablePanelClassName}
          style={{ "--schedule-row-height": `${SCHEDULE_ROW_HEIGHT}px` }}
        >
          <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600">
            Tasks
          </div>
          <div
            ref={tableScrollRef}
            onScroll={handleTableScroll}
            className={tableScrollClassName}
            style={{ height: tableBodyHeight, maxHeight: tableBodyHeight }}
          >
            <div style={{ minWidth: tableScrollX }}>
              <Table
                columns={columns}
                dataSource={schedule.tree}
                pagination={false}
                size="small"
                rowKey="key"
                sticky
                tableLayout="fixed"
                expandable={{
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: setExpandedKeys,
                  indentSize: 16,
                }}
                rowClassName={(record) => {
                  if (showCriticalPath && record.isCritical && !record.isSummary) {
                    return "[&_td]:!bg-orange-50";
                  }
                  if (record.isSummary) return "[&_td]:!bg-slate-50";
                  return "";
                }}
                className="[&_.ant-table-tbody>tr>td]:!h-[var(--schedule-row-height)]"
              />
            </div>
          </div>
        </div>
        <div className="flex min-h-0 flex-col overflow-hidden">
          <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600">
            Timeline
          </div>
          <ScheduleGanttChart
            rows={visibleRows}
            dependencies={schedule.dependencies}
            showCriticalPath={showCriticalPath}
            showPercentComplete={showPercentComplete}
            showDependencies={showDependencies}
            timeScale={timeScale}
            scrollHeight={tableBodyHeight}
            scrollRef={ganttScrollRef}
            onScroll={handleGanttScroll}
          />
        </div>
      </div>
    </div>
  );
};

export default ScheduleViewer;
